import type { ObjectValues } from "@/src/shared/types/objectValues";
import { DS_SIZE } from "@/src/design_system/shared/types/size";
import type { DSIconName } from "@/src/design_system/primitives/icon/types/iconName.generated";
import type { DSColorPalette } from "@/src/design_system/shared/types/colorPalette.generated";
import type { DSAriaLabelProps, DSSizingProps } from "@/src/design_system/shared/types/props";

export const DS_MENU_ITEM_TYPE = {
  Item: "item",
  Separator: "separator",
} as const;

export const DS_MENU_ITEM_SEVERITY = {
  Default: "default",
  Danger: "danger",
} as const;

export type DSMenuItemType = ObjectValues<typeof DS_MENU_ITEM_TYPE>;
export type DSMenuItemSeverity = ObjectValues<typeof DS_MENU_ITEM_SEVERITY>;

export interface DSMenuActionItem {
  type?: typeof DS_MENU_ITEM_TYPE.Item;
  /** Globally unique value within the complete menu tree. */
  value: string;
  label: string;
  href?: string;
  turboFrame?: string;
  turboMethod?: string;
  turboConfirm?: string;
  subtitle?: string;
  icon?: DSIconName;
  color?: DSColorPalette | null;
  prefixProps?: Record<string, unknown>;
  suffixProps?: Record<string, unknown>;
  severity?: DSMenuItemSeverity;
  /** Marks a navigation item with href as the current destination. */
  selected?: boolean;
  disabled?: boolean;
  /** Overrides the `data-cy` of the item's action element. Defaults to `ds-menu__item-content--<value>`, value normalized for selectors. */
  dataCy?: string;
  children?: DSMenuItem[];
}

export interface DSMenuSeparatorItem {
  type: typeof DS_MENU_ITEM_TYPE.Separator;
  /** Globally unique value within the complete menu tree. */
  value: string;
  label?: string;
}

export type DSMenuItem = DSMenuActionItem | DSMenuSeparatorItem;

export type DSMenuSize = (typeof DS_SIZE)["Small" | "Medium" | "Large"];

export type DSMenuProps = DSSizingProps<DSMenuSize> &
  Pick<DSAriaLabelProps, "ariaLabel"> & {
    items?: DSMenuItem[];
    expandedValues?: string[];
  };
